import { shouldShowNavigationLink } from 'utils/cms/navigationHelpers';

const CMS_MANAGED_THUNDR_PAGES = ['boltGallery', 'boltHome', 'boltHomeEnt', 'boltPreviewPanel', 'boltShowtimes', 'boltSynopsis', 'boltVideos', 'boltWatchMore'];

function pageIsDisabled(pageId, cmsData) {
	if (pageId === 'home') return false; // Home is always enabled
	return !cmsData?.pages?.[pageId]?.active;
}

function getCmsDefaultPages(cmsData) {
	const homeThundrId = cmsData?.pages?.home?.heroMode === 'preview' ? 'boltPreviewPanel' : 'boltHome';
	return [
		{ id: 'home', thundrId: homeThundrId, name: 'Home', order: 0 },
		{ id: 'synopsis', thundrId: 'boltSynopsis', name: 'Synopsis', order: 1 },
		{ id: 'gallery', thundrId: 'boltGallery', name: 'Gallery', order: 2 },
		{ id: 'videos', thundrId: 'boltVideos', name: 'Videos', order: 3 },
		{ id: 'showtimes', thundrId: 'boltShowtimes', name: 'Showtimes', order: 4 },
		{ id: 'homeEnt', thundrId: 'boltHomeEnt', name: 'Watch at Home', order: 5 },
	];
}

function getText(value, appData) {
	const { accessibilityCopy, baseCopy } = appData?.copy || {};
	return baseCopy?.[value] || accessibilityCopy?.[value] || value || '';
}

function getDefaultLinks(cmsData, appData) {
	const thundrPages = appData?.pages || {};

	// Map CMS defaults
	const defaultPages = Object.fromEntries(
		getCmsDefaultPages(cmsData)
			.map((page, i) => {
				const thundrPage = thundrPages[page.thundrId];
				const data = {
					...page,
					name: getText(thundrPage?.name || page.name, appData),
					order: i,
					default: true,
					permanent: page.id === 'home',
				};

				return [page.id, data];
			})
	);

	// Add any additional Bolt pages that aren't in the CMS defaults
	Object.entries(thundrPages)
		.filter(([thundrId, pageData]) => pageData.base === 'bolt' && !CMS_MANAGED_THUNDR_PAGES.includes(thundrId))
		.forEach(([thundrId, pageData]) => {
			const name = getText(pageData.name || thundrId, appData); // Should use copy variable value if available
			const destination = thundrId || pageData.path;
			const order = Object.keys(defaultPages).length; // Add to end of list
			defaultPages[thundrId] = { id: thundrId, name, destination, order };
		});

	return defaultPages;
}

function mapNav(cmsData, appData) {
	const links = cmsData?.header?.links || getDefaultLinks(cmsData, appData);

	// Recompute disabled state from current page toggles so saved/custom links
	// reflect the latest toggle values (ensureOverride bakes in a stale disabled flag).
	// Showtimes/homeEnt visibility is status-driven (ticket dates, homeEnt dates) so
	// we clear their disabled flag and let shouldShowNavigationLink handle them.
	const statusDrivenPages = ['showtimes', 'homeEnt'];
	Object.values(links).forEach(link => {
		if (!link.id || !cmsData?.pages?.[link.id]) return;
		if (statusDrivenPages.includes(link.id)) {
			delete link.disabled;
		} else {
			link.disabled = pageIsDisabled(link.id, cmsData);
		}
	});

	// Process to regular links
	const homeThundrId = cmsData?.pages?.home?.heroMode === 'preview' ? 'boltPreviewPanel' : 'boltHome';
	const nav = Object.values(links)
		.filter(link => shouldShowNavigationLink(link, cmsData))
		.reduce((acc, item) => {
			acc[item.id] = {
				linkType: 'primary',
				name: item.name,
				order: item.order,
				path: item.id === 'home' ? homeThundrId : (item.thundrId || item.destination), // Ensure home links to the correct Thundr page based on hero mode
			};
			return acc;
		}, {});

	return nav;
}

function mapSocials(cmsData) {
	const socialsData = cmsData?.pages?.home?.socials;
	if (!socialsData) return {};

	const { facebook, instagram, tiktok, twitter, youtube } = socialsData;
	return {
		facebook: facebook?.enabled ? facebook : null,
		instagram: instagram?.enabled ? instagram : null,
		tiktok: tiktok?.enabled ? tiktok : null,
		twitter: twitter?.enabled ? twitter : null,
		youtube: youtube?.enabled ? youtube : null,
	};
}

export default function mapHeader(cmsData, appData) {
	return {
		powsterHeader: {
			follow: mapSocials(cmsData),
			nav: mapNav(cmsData, appData),
		}
	};
}